import {
  CardOverlayWorkflowDto,
  HapticFeedbackDto,
  HapticFeedbackType,
} from '@bluecodecom/ui-sdk-abstraction';

import {
  CameraListener,
  InitiateMerchantTokenPaymentProps,
  NativePlatform,
  OpenWebviewOverlayProps,
  VasAssertionRequestProps,
} from './types';

const ALLOWED_MESSAGE_ORIGIN_DOMAINS = [
  'bluecode.mobi',
  'bluecode.biz',
  'bluecode.com',
  'bluecode.ng',
  'spt-payments.mobi',
  'spt-payments.biz',
  'spt-payments.com',
];

/**
 * Origins the host tooling is served from while developing — the simulator on
 * `localhost:4471`, or an app behind the HAProxy `*.bloomco.de` mapping.
 *
 * Without these, every `BC_NATIVE_CB` a locally served simulator posts into a
 * mini is dropped by the check below, so a bridge callback can only ever be
 * exercised against a deployed host. Kept out of production builds entirely:
 * webpack replaces `process.env.NODE_ENV` at build time, so the array folds to
 * empty in anything shipped.
 */
const ALLOWED_DEVELOPMENT_ORIGIN_DOMAINS =
  process.env['NODE_ENV'] === 'production' ? [] : ['localhost', 'bloomco.de'];

const EXTERNAL_BROWSER_PROTOCOL = 'externalbrowser';

/**
 * First SDK release carrying `native_bc_launch_camera` — Android 7.2.75
 * (`bc-sdk-android` commit `2f13ad23`), and iOS in its own 7.2.75 two weeks
 * later (`bc-sdk-ios` commit `09d3f1cc`), which is the lockstep this number was
 * chosen on the strength of.
 *
 * Exported so a mini can gate itself on the same number it capability-checks
 * against, rather than restating it.
 */
export const MIN_CAMERA_SDK_VERSION = '7.2.75';

/**
 * First SDK release whose camera can open the photo library instead — iOS
 * 7.2.77 (`bc-sdk-ios` commit `6b27b880`).
 *
 * Android does not have the mode yet and is expected to add it; the check is
 * the version alone rather than the platform — see
 * {@link NativeService.isPhotoLibrarySupported} for what that costs meanwhile.
 */
export const MIN_PHOTO_LIBRARY_SDK_VERSION = '7.2.77';

class NativeService {
  private static instance: NativeService;

  private _isInOverlay = false;

  private cameraListeners = new Set<CameraListener>();

  /**
   * The listener that launched the camera currently on screen, and therefore
   * the only one a result belongs to.
   *
   * A capture screen mounts one listener per slot, and the SDK's callbacks are
   * two global `window` properties carrying nothing that identifies the
   * request — so without this, one photo would land in every slot at once.
   */
  private activeCameraListener: CameraListener | null = null;

  public get isInOverlay() {
    return this._isInOverlay;
  }

  public set isInOverlay(value) {
    this._isInOverlay = value;
  }

  // Returns the cached instance — it used to hand back a fresh `new
  // NativeService()`, so construction ran twice and each run registered another
  // `message` listener, and the exported singleton was not the object stored
  // here (making `isInOverlay` writes invisible to it).
  public static getInstance() {
    if (!NativeService.instance) {
      NativeService.instance = new NativeService();
    }

    return NativeService.instance;
  }

  constructor() {
    window.addEventListener('message', (e: MessageEvent) => {
      const hostname =
        URL.parse(e.origin)?.hostname.split('.').slice(-2).join('.') || '';

      if (
        !ALLOWED_MESSAGE_ORIGIN_DOMAINS.includes(hostname) &&
        !ALLOWED_DEVELOPMENT_ORIGIN_DOMAINS.includes(hostname)
      ) {
        // console.log('Blocked message from untrusted origin:', e.origin);

        return;
      }

      if (typeof e.data !== 'object') {
        return;
      }

      const data = e.data as BluecodeNativeReceiveMessage;

      if (data.type !== 'BC_NATIVE_CB') {
        return;
      }

      switch (data.fn) {
        case 'isFullscreen': {
          if (
            typeof data.parameter === 'boolean' ||
            typeof data.parameter === 'string'
          ) {
            return window.isFullscreen?.(data.parameter);
          }

          break;
        }
        case 'externalSetLocation': {
          if (typeof data.parameter === 'object') {
            const { latitude, longitude } = data.parameter as {
              latitude: number;
              longitude: number;
            };

            if (typeof latitude === 'number' && typeof longitude === 'number') {
              return window.externalSetLocation?.(latitude, longitude);
            }
          }

          break;
        }
        case 'can_pay': {
          if (
            typeof data.parameter === 'boolean' ||
            typeof data.parameter === 'string'
          ) {
            return window.can_pay?.(data.parameter);
          }

          break;
        }
        case 'viewContextNameType': {
          return window.viewContextNameType?.();
        }
        case 'sdkLocked': {
          return window.sdkLocked?.();
        }
        case 'onVasAssertionResult': {
          if (typeof data.parameter === 'string') {
            return window.onVasAssertionResult?.(data.parameter);
          }

          break;
        }
        case 'receive_tracking_event': {
          if (typeof data.parameter === 'string') {
            return window.receive_tracking_event?.(
              data.parameter as BluecodeNativeTrackingEventType,
            );
          }

          break;
        }
        case 'setContractInfo': {
          if (typeof data.parameter === 'string') {
            return window.setContractInfo?.(data.parameter);
          }

          break;
        }
        case 'setHasSuggestionsEnabled': {
          if (typeof data.parameter === 'boolean') {
            return window.setHasSuggestionsEnabled?.(data.parameter);
          }

          break;
        }
        case 'setVasDeepLink': {
          if (typeof data.parameter === 'string') {
            return window.setVasDeepLink?.(data.parameter);
          }

          break;
        }
        case 'setVasHostConfig': {
          if (typeof data.parameter === 'string') {
            return window.setVasHostConfig?.(data.parameter);
          }

          break;
        }
        case 'setVasMerchant': {
          if (typeof data.parameter === 'string' || data.parameter === null) {
            return window.setVasMerchant?.(data.parameter);
          }

          break;
        }
        case 'set_loyalty_data': {
          if (typeof data.parameter === 'string') {
            return window.set_loyalty_data?.(data.parameter);
          }

          break;
        }
        // Relayed rather than assumed native-only: inside the simulator the
        // camera is mocked by the host page, which posts the result down to
        // this frame instead of calling `window` on it.
        case 'onCameraDidCapturePhoto': {
          if (typeof data.parameter === 'string') {
            return window.onCameraDidCapturePhoto?.(data.parameter);
          }

          break;
        }
        case 'onCameraDidScanCode': {
          if (typeof data.parameter === 'string') {
            return window.onCameraDidScanCode?.(data.parameter);
          }

          break;
        }
      }
    });
  }

  public validateUrl(next: string, protocol = 'https') {
    try {
      const url = new URL(next || '');
      return `${protocol}://${url.host}${url.pathname}${url.search}${url.hash}`;
    } catch {
      return '';
    }
  }

  public isInIframe() {
    return window !== window.parent;
  }

  public runJsFunction(
    fn: BluecodeNativeFnName,
    parameter?: BluecodeNativeArg,
  ) {
    if (this.isInIframe()) {
      return window.parent.postMessage(
        { fn, parameter, type: 'BC_NATIVE_FN' } as BluecodeNativePostMessage,
        '*',
      );
    }

    try {
      if (window.webkit?.messageHandlers?.[fn]?.postMessage) {
        return (
          window.webkit.messageHandlers[fn].postMessage as BluecodeNativeFn
        )(parameter || 'run');
      }

      if (window.bluecode?.[fn]) {
        if (!parameter) {
          return window.bluecode[fn]();
        } else {
          if (typeof parameter === 'object') {
            parameter = JSON.stringify(parameter);
          }

          return window.bluecode[fn](parameter);
        }
      }

      console.log('The native context does not exist yet');
    } catch {
      console.log(`There was an issue with native context for function ${fn}`);
    }
  }

  public hasNativeJsFunction(fnName: BluecodeNativeFnName) {
    return Boolean(
      window.webkit?.messageHandlers[fnName] || window.bluecode?.[fnName],
    );
  }

  /**
   * Check if window object has webkit.messageHandlers or bluecode
   * objects. If yes we are running JS inside the native webview
   * @returns boolean
   */
  public isNativeEnvironment() {
    return Boolean(window.webkit?.messageHandlers || window.bluecode);
  }

  public askGeo() {
    this.runJsFunction('native_bc_ask_geo');
  }

  public appFocus() {
    this.runJsFunction('native_bc_app_focus');
  }

  public setVasFullscreen(isFullscreen: boolean) {
    this.runJsFunction(
      'native_bc_set_vas_fullscreen',
      isFullscreen ? 'true' : 'false',
    );
  }

  public isVasFullscreen() {
    this.runJsFunction('native_bc_is_vas_fullscreen');
  }

  public openBluecodeSettings() {
    this.runJsFunction('native_bc_open_bluecode_settings');
  }

  public minimizeWebview() {
    this.runJsFunction('native_bc_minimize_webview');
  }

  public vibrate() {
    this.runJsFunction('native_bc_vibrate');
  }

  public playSound(sound = 'notification') {
    this.runJsFunction('native_bc_play_sound', sound);
  }

  public openInternal(url: string) {
    this.runJsFunction('native_bc_open_internal', url);
  }

  public openExternal(url: string) {
    switch (window.viewContextNameType?.()) {
      case 'onboardingContextType':
        return this.runJsFunction('native_bc_open_external', url);
      case 'miniAppContextType':
        window.location.href = this.validateUrl(url, EXTERNAL_BROWSER_PROTOCOL);
        return;
    }

    this.runJsFunction('native_bc_open_external', url);
  }

  public askLoyalty() {
    this.runJsFunction('native_bc_ask_loyalty');
  }

  public closeMiniApp() {
    this.runJsFunction('native_bc_close_miniapp');
  }

  public closeWebview() {
    this.runJsFunction('native_bc_close_webview');
  }

  public showNavigation() {
    this.runJsFunction('native_bc_show_navigation');
  }

  public hideNavigation() {
    this.runJsFunction('native_bc_hide_navigation');
  }

  public ultimateWebviewClose() {
    this.minimizeWebview();
    this.closeMiniApp();
    this.closeWebview();
  }

  public playSoundAndVibrate() {
    this.playSound();
    this.vibrate();
  }

  public openWebviewOverlay({
    url,
    title = null,
    miniAppId = null,
  }: OpenWebviewOverlayProps) {
    const options = {
      url,
      title,
      miniAppId,
      heightInPercent: null,
      options: null,
    };

    this.runJsFunction('native_bc_open_webview_overlay_with_url', options);
  }

  public initiateMerchantTokenPayment(
    props: InitiateMerchantTokenPaymentProps,
  ) {
    this.runJsFunction('native_bc_initiate_merchant_token_payment', props);
  }

  public startOnboarding() {
    this.runJsFunction('native_bc_start_onboarding');
  }

  public openHuaweiWalletPrivacyPage() {
    this.runJsFunction('open_huawei_wallet_privacy_page');
  }

  public refreshHistory() {
    this.runJsFunction('native_bc_refresh_history');
  }

  public requestReview() {
    this.runJsFunction('native_bc_request_review');
  }

  public shareString(message: string) {
    this.runJsFunction('native_bc_share_string', message);
  }

  public canPay() {
    this.runJsFunction('native_bc_can_pay');
  }

  public getSdkVersion() {
    const info = navigator.userAgent?.split('|');

    if (info.length !== 3) {
      return 'not new_webview';
    }

    const versionSearch = info[1].match(/\d+\.\d+\.\d+/g);

    if (!versionSearch) {
      return 'no version detected';
    }

    return versionSearch[0];
  }

  public isSdkAtLeast(compareToVersion: string) {
    const m = navigator.userAgent?.match(/BCSDK ([0-9]*)\.([0-9]*)\.([0-9]*)/);

    if (m) {
      const compareToEls = compareToVersion.split('.');
      const sdkEls = m.slice(1, 4);

      const toComparable = (els: string[]) =>
        els.map((el) => '000'.substring(el.length) + el).join('');

      return toComparable(sdkEls) >= toComparable(compareToEls);
    }

    return false;
  }

  /**
   * Which OS the SDK's webview is running on, read out of the user agent.
   *
   * The SDK *replaces* the webview user agent wholesale with a fixed
   * three-segment string, the third segment of which is the OS — built by
   * `DeviceInfo.kt` on Android and `DeviceInfo.swift` on iOS:
   *
   *     MobilePocket (appScheme) 1.23#3131 | BCSDK 1.00#121 | Android 14#build21, Samsung G3
   *     Bluecode (bluecode) 4711           | BCSDK 121       | iOS 17.4#21E219, iPhone15,2
   *
   * Hence the segment split rather than a substring test: the first segment is
   * a human-chosen app name and the last a device model, so a bare
   * `includes('Android')` would match on either.
   *
   * Like {@link isSdkAtLeast}, this errs towards not knowing. A plain browser,
   * the sdk-simulator and jsdom all carry an ordinary user agent with no such
   * marker and get `'unknown'`, never a guess — so callers must read
   * `'unknown'` as "do not enable platform-specific behaviour", and never as
   * "the other platform".
   *
   * This is the right check *inside* the SDK webview. `react-device-detect`,
   * used by merchant-sdk-launcher, invite and sdk-selector, parses a real
   * browser user agent: it happens to match `Android 14` here by regex luck and
   * reads `iOS 17.4` as an unknown OS, so it has no contract with this format.
   */
  public getPlatform(): NativePlatform {
    const segments = navigator.userAgent?.split('|');

    if (segments?.length !== 3) {
      return 'unknown';
    }

    const os = segments[2].trim();

    if (/^Android(\s|$)/.test(os)) {
      return 'android';
    }

    if (/^iOS(\s|$)/.test(os)) {
      return 'ios';
    }

    return 'unknown';
  }

  public isAndroid() {
    return this.getPlatform() === 'android';
  }

  public isIos() {
    return this.getPlatform() === 'ios';
  }

  public supportsMiniAppWebView() {
    return this.isSdkAtLeast('5.1.0');
  }

  public supportsOverlayHeaderHiding() {
    return (
      !nativeService.isNativeEnvironment() ||
      (window.viewContextNameType?.() === 'miniAppContextType' &&
        nativeService.isSdkAtLeast('6.5.13') &&
        this.hasNativeJsFunction('native_bc_hide_navigation'))
    );
  }

  public hasCustomNavigation() {
    if (!this.supportsOverlayHeaderHiding()) {
      return false;
    }

    const url = window.location.href;

    return (
      url.includes('hasCustomNavigation=true') ||
      url.includes('display=with-back-button') ||
      url.includes('withCustomNavigation=true')
    );
  }

  public openUrl({ url, title = '', miniAppId }: OpenWebviewOverlayProps) {
    if (
      !this.isInOverlay &&
      (this.supportsMiniAppWebView() || this.isInIframe())
    ) {
      return this.openWebviewOverlay({
        url,
        miniAppId,
        title,
      });
    }

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.click();
    anchor.remove();
  }

  /**
   * The app's own scanner, which takes over the screen.
   *
   * Deliberately **not** the same thing as `launchCamera('scan-code')`: that
   * one is an overlay drawn above a mini that stays mounted behind it. Both
   * exist, and this is the only one iOS has today.
   */
  public scanCode() {
    this.runJsFunction('native_bc_scan_code');
  }

  /**
   * Whether the SDK can open a camera over a mini without closing it.
   *
   * Both halves are needed. The probe is what answers correctly *today*: iOS
   * registers each bridge name individually, so the handler is simply absent
   * there until its 7.2.75 ships. The version is what keeps answering
   * correctly *later*, once every platform has some build with the feature.
   *
   * The one way this errs is towards `false` — `isSdkAtLeast` reads the SDK
   * version out of the user agent and says no when the marker is missing. That
   * costs a fall back to the file input; the opposite error would be a camera
   * button that does nothing at all.
   */
  public isCameraSupported() {
    // Escape hatch in the same spirit as `hasCustomNavigation`'s URL flags:
    // append `noCamera=true` to a mini's URL to exercise its file-input
    // fallback — the path every iPhone takes until iOS 7.2.75 — without
    // needing a device that lacks the bridge.
    if (window.location.href.includes('noCamera=true')) {
      return false;
    }

    // A framed mini is one the SDK simulator is hosting, and the simulator
    // answers `native_bc_launch_camera` itself — see `NativeBcHandler`, which
    // lands with this line. There is no bridge to probe and no SDK version in
    // the user agent there, so both checks below would say no. Development
    // builds only: nothing shipped can ask an arbitrary parent whether it
    // honours a camera request, so nothing shipped should assume one does.
    if (this.isInIframe() && process.env['NODE_ENV'] !== 'production') {
      return true;
    }

    return (
      this.hasNativeJsFunction('native_bc_launch_camera') &&
      this.isSdkAtLeast(MIN_CAMERA_SDK_VERSION)
    );
  }

  /**
   * Whether that camera can open the photo library instead of the viewfinder.
   *
   * Version-gated only, **deliberately**, even though today the mode is
   * iOS-only (7.2.77) and Android's router ignores a mode it does not know —
   * no camera, no callback, no error. So on an Android build claiming 7.2.77
   * this answers yes and the control does nothing, until Android ships the
   * mode. That was chosen over an `isIos()` clause: Android is implementing it,
   * and a platform gate is something we would have to remember to remove, with
   * nothing failing to remind us.
   *
   * Still not implied by {@link isCameraSupported} — a build can have the
   * camera and not the library, so ask this one before offering the library.
   *
   * A framed mini outside production is trusted the way {@link
   * isCameraSupported} trusts one, since the simulator answers the mode itself.
   */
  public isPhotoLibrarySupported() {
    if (!this.isCameraSupported()) {
      return false;
    }

    // Same trust `isCameraSupported` extends to a framed mini, needed a second
    // time because the version check below would otherwise veto it: the
    // simulator answers `open-photo-library` with the same file picker it
    // answers `take-photo` with, and a frame has no bridge to probe and no SDK
    // version in its user agent. Reached only after the check above, so
    // `noCamera=true` still switches the whole mini to its file-input path.
    if (this.isInIframe() && process.env['NODE_ENV'] !== 'production') {
      return true;
    }

    return this.isSdkAtLeast(MIN_PHOTO_LIBRARY_SDK_VERSION);
  }

  /**
   * Starts listening for camera results. Returns the unsubscribe.
   *
   * Subscribing alone receives nothing — results go to whichever listener most
   * recently called {@link launchCamera}.
   */
  public subscribeToCamera(listener: CameraListener) {
    this.installCameraBridge();
    this.cameraListeners.add(listener);

    return () => {
      this.cameraListeners.delete(listener);

      if (this.activeCameraListener === listener) {
        this.activeCameraListener = null;
      }
    };
  }

  /**
   * Opens the camera and marks `listener` as the owner of whatever comes back.
   *
   * Fire-and-forget on purpose: a dismissed camera and a denied permission both
   * report **nothing** to the webview, so a caller that waits for a result
   * waits forever. Launch, keep the control usable, and let a result arrive if
   * it does.
   */
  public launchCamera(
    mode: BluecodeNativeCameraMode,
    listener?: CameraListener,
  ) {
    if (listener) {
      this.installCameraBridge();
      this.activeCameraListener = listener;
    }

    this.runJsFunction('native_bc_launch_camera', mode);
  }

  /**
   * Claims the two `window` callbacks for the service.
   *
   * Re-assigns on every call rather than installing once behind a flag:
   * anything else on the page can overwrite or delete these properties, and a
   * one-shot install would then leave the service permanently deaf with no
   * symptom other than results never arriving.
   */
  private installCameraBridge() {
    window.onCameraDidCapturePhoto = (base64Image: string) =>
      this.deliverCameraResult((listener) => listener.onPhoto?.(base64Image));

    window.onCameraDidScanCode = (code: string) =>
      this.deliverCameraResult((listener) => listener.onCode?.(code));
  }

  /**
   * Hands one result to the listener that asked for it, then forgets it — the
   * native overlay closes after a capture, so a launch yields at most one
   * result and the next one requires a new launch.
   *
   * A result whose listener unmounted in the meantime is dropped rather than
   * broadcast: nobody is waiting for it any more.
   */
  private deliverCameraResult(deliver: (listener: CameraListener) => void) {
    const listener = this.activeCameraListener;

    this.activeCameraListener = null;

    if (!listener || !this.cameraListeners.has(listener)) {
      return;
    }

    deliver(listener);
  }

  public setBadgeCount(count: number) {
    this.runJsFunction('native_bc_set_badge_count', count);
  }

  public requestSdkUnlock() {
    this.runJsFunction('native_bc_request_sdk_unlock');
  }

  public setMiniAppTrackingId(id: string) {
    this.runJsFunction('native_bc_set_mini_app_tracking_id', id);
  }

  public initiatePayment(props: InitiateMerchantTokenPaymentProps) {
    this.runJsFunction('native_bc_initiate_payment', props);
  }

  public vasAssertionRequest(props: VasAssertionRequestProps) {
    this.runJsFunction('native_bc_vas_assertion_request', props);
  }

  public navigateToMerchantSuggestions() {
    this.runJsFunction('native_bc_navigate_to_merchant_suggestions');
  }

  public allowResumeOnboarding() {
    this.runJsFunction('native_bc_allow_resume_onboarding');
  }

  public openExternalAndAllowResume(url: string) {
    switch (window.viewContextNameType?.()) {
      case 'onboardingContextType':
        return this.runJsFunction('native_bc_open_external_allow_resume', url);
      case 'miniAppContextType':
        this.allowResumeMiniApp();
        window.location.href = this.validateUrl(url, EXTERNAL_BROWSER_PROTOCOL);
        return;
    }

    return this.runJsFunction('native_bc_open_external_allow_resume', url);
  }

  public denyResumeOnboarding() {
    this.runJsFunction('native_bc_deny_resume_onboarding');
  }

  public requestVasHostConfig() {
    if (!window.setVasHostConfig) {
      // TODO: Sentry error - not set
    }

    this.runJsFunction('native_bc_request_vas_host_config');
  }

  public requestContractInfo() {
    if (!window.setContractInfo) {
      // TODO: Sentry error - not set
    }

    this.runJsFunction('native_bc_request_current_contract_info');
  }

  public setLocalPushNotification(notification: LocalPushNotification) {
    this.runJsFunction(
      'native_bc_set_local_push_notification',
      JSON.stringify(notification),
    );
  }

  public cancelLocalPushNotification(identifier: string) {
    const params: CancelLocalPushNotificationDto = {
      identifier,
    };

    this.runJsFunction(
      'native_bc_cancel_local_push_notification',
      JSON.stringify(params),
    );
  }

  public allowResumeMiniApp() {
    this.runJsFunction('native_bc_allow_resume_mini_app');
  }

  public denyResumeMiniApp() {
    this.runJsFunction('native_bc_deny_resume_mini_app');
  }

  public setOnboardingTrackingEvent(eventType: OnboardingTrackingEventType) {
    const params: OnboardingTrackingEvent = {
      eventType,
    };

    this.runJsFunction(
      'native_bc_set_onboarding_tracking_event',
      JSON.stringify(params),
    );
  }

  public setContentDeepLink(params: ContentDeepLinkDto) {
    this.runJsFunction(
      'native_bc_set_content_deep_link',
      JSON.stringify(params),
    );
  }

  public requestOpenOnboarding(bic?: string) {
    const payload: OpenOnboardingRequestDto = {
      bic: bic || null,
    };

    this.runJsFunction(
      'native_bc_request_open_onboarding',
      JSON.stringify(payload),
    );
  }

  public requestOpenBankAccountSettings() {
    this.runJsFunction('native_bc_request_open_bank_account_settings');
  }

  public setCardWorflowResult(dto: CardOverlayWorkflowDto) {
    this.runJsFunction(
      'native_bc_set_card_workflow_result',
      JSON.stringify(dto),
    );
  }

  public executeHapticFeedback(params?: HapticFeedbackType) {
    const payload: HapticFeedbackDto = {
      hapticFeedbackType: params || 'moderate',
    };

    this.runJsFunction(
      'native_bc_execute_haptic_feedback',
      JSON.stringify(payload),
    );
  }
}

const nativeService = NativeService.getInstance();

export default nativeService;
